import React, { useState, useEffect } from 'react';
import { apiService } from '../services/api';
import './ReceiptSubmitView.css';

function ReceiptSubmitView() {
  const [supportStaff, setSupportStaff] = useState([]);
  const [individuals, setIndividuals] = useState([]);
  const [selectedStaff, setSelectedStaff] = useState('');
  const [selectedIndividual, setSelectedIndividual] = useState('');
  const [receiptFiles, setReceiptFiles] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [message, setMessage] = useState(null);
  const [step, setStep] = useState('upload'); // 'upload', 'processing', 'review', 'submitting', 'complete'
  const [extractedData, setExtractedData] = useState([]);
  const [processingStatus, setProcessingStatus] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const staffData = await apiService.getCaretakers();
      const individualsData = await apiService.request('/receipts/individuals');
      setSupportStaff(staffData);
      setIndividuals(individualsData);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 5) {
      setMessage({ type: 'error', text: 'Maximum 5 images per shopping trip' });
      return;
    }
    setReceiptFiles(files);
    setMessage(null);
  };

  const removeFile = (index) => {
    setReceiptFiles(files => files.filter((_, i) => i !== index));
  };

  const handleProcessImages = async (e) => {
    e.preventDefault();
    
    if (!selectedStaff || !selectedIndividual || receiptFiles.length === 0) {
      setMessage({ type: 'error', text: 'Please fill in all fields' });
      return;
    }

    setProcessing(true);
    setStep('processing');
    setMessage(null);
    setProcessingStatus('Uploading images...');

    try {
      // Create FormData for file upload
      const formData = new FormData();
      receiptFiles.forEach((file) => {
        formData.append('files', file);
      });

      setProcessingStatus('Analyzing receipts with AI...');

      // Call the processing endpoint. credentials: 'include' is required so
      // the JWT session cookie is sent — without it the auth middleware
      // returns 401 and the browser then reports it as a CORS error.
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8001'}/receipts/process-images`, {
        method: 'POST',
        body: formData,
        credentials: 'include',
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Processing failed');
      }

      const result = await response.json();
      
      setProcessingStatus('Extraction complete!');
      // Default currency to CAD if not set
      const receiptsWithDefaults = result.receipts.map(receipt => ({
        ...receipt,
        currency: receipt.currency || 'CAD'
      }));
      setExtractedData(receiptsWithDefaults);
      setStep('review');
      setMessage({ 
        type: 'success', 
        text: `Successfully extracted data from ${result.receipts.length} receipt(s). Please review and approve.` 
      });
      
    } catch (error) {
      console.error('Processing error:', error);
      setMessage({ type: 'error', text: error.message || 'Failed to process receipts' });
      setStep('upload');
    } finally {
      setProcessing(false);
    }
  };

  const handleDataChange = (index, field, value) => {
    setExtractedData(data => {
      const newData = [...data];
      newData[index][field] = value;
      return newData;
    });
  };

  const handleSubmitApproved = async () => {
    setStep('submitting');
    setProcessing(true);
    setProcessingStatus('Creating receipt records...');

    try {
      // Submit each receipt
      const promises = extractedData.map(receipt => 
        apiService.request('/receipts/create-from-upload', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            caretaker_id: parseInt(selectedStaff),
            individual_id: parseInt(selectedIndividual),
            store_name: receipt.store_name,
            purchase_date: receipt.purchase_date,
            total_amount: parseFloat(receipt.total_amount),
            currency: receipt.currency,
            temp_image_path: receipt.temp_image_path,
            flag_reason: receipt.flag_reason
          })
        })
      );

      await Promise.all(promises);

      setStep('complete');
      setMessage({ 
        type: 'success', 
        text: `Successfully created ${extractedData.length} receipt(s)! They are now pending approval.` 
      });

      // Reset form after 3 seconds
      setTimeout(() => {
        resetForm();
      }, 3000);
      
    } catch (error) {
      console.error('Submission error:', error);
      setMessage({ type: 'error', text: 'Failed to create receipts. Please try again.' });
      setStep('review');
    } finally {
      setProcessing(false);
    }
  };

  const resetForm = () => {
    setSelectedStaff('');
    setSelectedIndividual('');
    setReceiptFiles([]);
    setExtractedData([]);
    setStep('upload');
    setMessage(null);
  };

  return (
    <div className="submit-view">
      <div className="view-header">
        <h1 className="view-title">Submit Receipt</h1>
        <p className="view-subtitle">Upload receipt images for AI processing</p>
      </div>

      {/* Progress Indicator */}
      <div className="progress-steps">
        <div className={`progress-step ${step === 'upload' || step === 'processing' ? 'active' : step === 'review' || step === 'submitting' || step === 'complete' ? 'completed' : ''}`}>
          <div className="step-number">1</div>
          <div className="step-label">Upload</div>
        </div>
        <div className="progress-line"></div>
        <div className={`progress-step ${step === 'processing' ? 'active' : step === 'review' || step === 'submitting' || step === 'complete' ? 'completed' : ''}`}>
          <div className="step-number">2</div>
          <div className="step-label">Process</div>
        </div>
        <div className="progress-line"></div>
        <div className={`progress-step ${step === 'review' || step === 'submitting' ? 'active' : step === 'complete' ? 'completed' : ''}`}>
          <div className="step-number">3</div>
          <div className="step-label">Review</div>
        </div>
        <div className="progress-line"></div>
        <div className={`progress-step ${step === 'submitting' || step === 'complete' ? 'active' : ''}`}>
          <div className="step-number">4</div>
          <div className="step-label">Submit</div>
        </div>
      </div>

      <div className="submit-form-container">
        {/* Upload Form */}
        {(step === 'upload' || step === 'processing') && (
          <form onSubmit={handleProcessImages} className="submit-form">
            <div className="form-group">
              <label htmlFor="staff">Support Staff Member *</label>
              <select
                id="staff"
                value={selectedStaff}
                onChange={(e) => setSelectedStaff(e.target.value)}
                required
                disabled={processing}
              >
                <option value="">Select support staff...</option>
                {supportStaff.map((staff) => (
                  <option key={staff.id} value={staff.id}>
                    {staff.name} - {staff.phone_number}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="individual">Individual (Person Supported) *</label>
              <select
                id="individual"
                value={selectedIndividual}
                onChange={(e) => setSelectedIndividual(e.target.value)}
                required
                disabled={processing}
              >
                <option value="">Select individual...</option>
                {individuals.map((individual) => (
                  <option key={individual.id} value={individual.id}>
                    {individual.name} - {individual.patient_id}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="receipts">Receipt Images * (One Shopping Trip)</label>
              <div className="file-upload-area">
                <input
                  type="file"
                  id="receipts"
                  accept="image/*"
                  multiple
                  onChange={handleFileChange}
                  required
                  disabled={processing}
                />
                {receiptFiles.length > 0 && (
                  <div className="files-preview">
                    {receiptFiles.map((file, index) => (
                      <div key={index} className="file-preview-item">
                        <span className="file-icon">📄</span>
                        <span className="file-name">{file.name}</span>
                        <span className="file-size">
                          ({(file.size / 1024).toFixed(0)} KB)
                        </span>
                        {!processing && (
                          <button 
                            type="button"
                            className="remove-file-btn"
                            onClick={() => removeFile(index)}
                          >
                            ✕
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <small className="form-hint">
                Upload 1-5 images from the same shopping trip • JPG, PNG, HEIC • Max 5MB each
              </small>
            </div>

            {processing && (
              <div className="processing-status">
                <div className="spinner"></div>
                <p>{processingStatus}</p>
              </div>
            )}

            {message && !processing && (
              <div className={`form-message ${message.type}`}>
                {message.type === 'success' ? '✓' : '⚠️'} {message.text}
              </div>
            )}

            <button
              type="submit"
              className="submit-btn"
              disabled={processing}
            >
              {processing ? (
                <>
                  <span className="spinner"></span>
                  Processing with AI...
                </>
              ) : (
                <>
                  <span>🤖</span>
                  Process with AI
                </>
              )}
            </button>
          </form>
        )}

        {/* Review Extracted Data */}
        {step === 'review' && (
          <div className="review-section">
            <div className="review-header">
              <h2>Review Extracted Information</h2>
              <p>Please verify the information extracted from your receipts. You can edit any field if needed.</p>
            </div>

            {message && (
              <div className={`form-message ${message.type}`}>
                {message.type === 'success' ? '✓' : '⚠️'} {message.text}
              </div>
            )}

            <div className="extracted-receipts">
              {extractedData.map((receipt, index) => (
                <div key={index} className="extracted-receipt-card">
                  <div className="receipt-card-header">
                    <h3>Receipt {index + 1}</h3>
                    {receipt.flag_reason && (
                      <span className="confidence-badge low">
                        ⚠️ {receipt.flag_reason}
                      </span>
                    )}
                    {!receipt.flag_reason && (
                      <span className="confidence-badge high">
                        ✓ {receipt.confidence}
                      </span>
                    )}
                  </div>

                  <div className="receipt-fields">
                    <div className="field-group">
                      <label>Store Name</label>
                      <input
                        type="text"
                        value={receipt.store_name}
                        onChange={(e) => handleDataChange(index, 'store_name', e.target.value)}
                      />
                    </div>

                    <div className="field-group">
                      <label>Purchase Date</label>
                      <input
                        type="date"
                        value={receipt.purchase_date}
                        onChange={(e) => handleDataChange(index, 'purchase_date', e.target.value)}
                      />
                    </div>

                    <div className="field-row">
                      <div className="field-group">
                        <label>Total Amount</label>
                        <input
                          type="number"
                          step="0.01"
                          value={receipt.total_amount}
                          onChange={(e) => handleDataChange(index, 'total_amount', e.target.value)}
                        />
                      </div>

                      <div className="field-group">
                        <label>Currency</label>
                        <select
                          value={receipt.currency}
                          onChange={(e) => handleDataChange(index, 'currency', e.target.value)}
                        >
                          <option value="CAD">CAD</option>
                          <option value="USD">USD</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="review-actions">
              <button className="btn-secondary" onClick={resetForm}>
                Cancel & Start Over
              </button>
              <button className="btn-primary" onClick={handleSubmitApproved}>
                <span>✓</span>
                Approve & Submit
              </button>
            </div>
          </div>
        )}

        {/* Complete State */}
        {step === 'complete' && (
          <div className="complete-section">
            <div className="complete-icon">✓</div>
            <h2>Receipts Submitted Successfully!</h2>
            <p>Your receipts are now pending admin approval.</p>
            <button className="btn-primary" onClick={resetForm}>
              Submit More Receipts
            </button>
          </div>
        )}

        {/* Info Sidebar - Only show on upload step */}
        {step === 'upload' && (
          <div className="submit-info">
            <h3>How it works</h3>
            <ol>
              <li>Select the support staff member who made the purchase</li>
              <li>Select the individual the purchase was for</li>
              <li>Upload 1-5 clear photos from ONE shopping trip</li>
              <li>AI extracts store name, date, and total automatically</li>
              <li>Review and edit the extracted information</li>
              <li>Submit for admin approval</li>
            </ol>

            <div className="info-note">
              <strong>Multiple Images:</strong> If you have multiple photos of the same receipt 
              (front/back, or separate items), upload them all together. One shopping trip = one submission.
            </div>

            <div className="info-note">
              <strong>Image Quality:</strong> Make sure receipts are clear, well-lit, and all text is readable for best AI accuracy.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ReceiptSubmitView;
